import { Component, EventEmitter, Output, ViewChild, OnInit, Input } from '@angular/core';

@Component({
    selector: 'redispatch-upload-file-form',
    templateUrl: './upload-file-form.component.html',
    styleUrls: [
        './upload-file-form.component.scss',
    ],
    standalone: false,
})
export class UploadFileFormComponent implements OnInit {

    @Input() allowedFileMimeType: string;
    @Input() allowedFileTypeName: string;
    @Output() fileEventEmitter = new EventEmitter<File>();

    @ViewChild('fileInput', { static: true }) file!: { nativeElement: { files: FileList } };

    selectedFile!: File;
    isDragging = false;
    isInvalidFileFormat = false;
    areMultipleFiles = false;

    ngOnInit() {
        this.selectedFile = undefined;
    }

    /**
     * Check if a file has a valid format.
     * @param fileType The MIME type of the file to check.
     * @returns True if the file has a valid format, false otherwise.
     */
    private isValidFileFormat(fileType: string): boolean {
        return fileType === this.allowedFileMimeType;
    }

    /**

     Checks if there are any validation violations for the selected file(s).
     Returns true if the selected file has an invalid format or if multiple files are selected.
     Otherwise, returns false.
     */
    hasValidationViolations(): boolean {
        return this.isInvalidFileFormat || this.areMultipleFiles;
    }

    /**
     * Handle when the file input changes (i.e., a file is selected).
     */
    onFileChanged(): void {
        // Reset the selected file to undefined
        this.selectedFile = undefined;

        // Get the files from the file input element
        const files = this.file.nativeElement.files;

        // If there is at least one file and it has a valid format, emit the file and update the selected file property
        if (files.length > 0 && this.isValidFileFormat(files[0].type)) {
            const selectedFile = files[0];
            this.fileEventEmitter.emit(selectedFile);
            this.selectedFile = selectedFile;
        }
    }

    /**
     * Handle when a file is dragged over the drop zone.
     * @param event The DragEvent object.
     */
    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.handleDraggedFile(event);
    }

    /**
     * Handle a dragged file.
     * @param event The DragEvent object.
     */
    private handleDraggedFile(event: DragEvent): void {
        this.isDragging = true;
        const files = event.dataTransfer.items;

        if (files.length !== 1) {
            this.isDragging = false;
            this.areMultipleFiles = true;
            return;
        }

        if (!this.isValidFileFormat(files[0].type)) {
            this.isInvalidFileFormat = true;
            this.isDragging = false;
        }

    }

    /**
     * Handle when a file is dropped in the drop zone.
     * @param event The DragEvent object.
     */
    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
        this.isInvalidFileFormat = false;
        this.areMultipleFiles = false;
        this.file.nativeElement.files = event.dataTransfer.files;
        this.onFileChanged();
    }

    /**
     * Handle when a dragged file leaves the drop zone.
     * @param event The DragEvent object.
     */
    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
        this.isInvalidFileFormat = false;
        this.areMultipleFiles = false;
    }
}
